import type { ComponentType } from 'react';

import type {
  ConnectionTestResponse,
  HealthResponse,
  Item
} from '../api';

export interface FeatureConfigInput {
  slug: unknown;
  name: unknown;
  description: unknown;
  requiresAuth?: unknown;
  availableWhenSignedOut?: unknown;
  featureType?: unknown;
  order?: unknown;
  tags?: unknown;
}

export interface FeatureConfig {
  slug: string;
  name: string;
  description: string;
  requiresAuth: boolean;
  availableWhenSignedOut: boolean;
  featureType: string;
  order: number;
  tags: string[];
}

export interface FeatureContext {
  isAuthenticated: boolean;
  apiBaseUrl: string;
  fallbackBaseUrl: string;
  shouldExplainFallback: boolean;
  userObjectId: string | null;
  health: HealthResponse | null;
  connectionTest: ConnectionTestResponse | null;
  items: Item[];
  itemsLoading: boolean;
  itemsError: string | null;
  acquireAccessToken: (() => Promise<string>) | null;
}

export type FeatureComponent = ComponentType<FeatureContext>;

export interface FeatureModule {
  config: FeatureConfig;
  Component: FeatureComponent;
}

export function normalizeFeatureConfig(
  raw: FeatureConfigInput,
  sourceLabel: string
): FeatureConfig {
  const slug = typeof raw.slug === 'string' ? raw.slug.trim() : '';
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const description =
    typeof raw.description === 'string' ? raw.description.trim() : '';
  const featureType =
    typeof raw.featureType === 'string' && raw.featureType.trim().length > 0
      ? raw.featureType.trim()
      : 'general';
  const requiresAuth = Boolean(raw.requiresAuth);
  const availableWhenSignedOutRaw = Boolean(raw.availableWhenSignedOut);
  const order = Number(raw.order);

  if (!slug) {
    throw new Error(`Feature configuration ${sourceLabel} is missing a slug.`);
  }

  if (!name) {
    throw new Error(
      `Feature configuration ${sourceLabel} is missing a human-readable name.`
    );
  }

  if (!description) {
    throw new Error(
      `Feature configuration ${sourceLabel} is missing a description.`
    );
  }

  if (!Number.isFinite(order)) {
    throw new Error(
      `Feature configuration ${sourceLabel} must include a numeric order.`
    );
  }

  const normalizedAvailableWhenSignedOut = requiresAuth
    ? false
    : availableWhenSignedOutRaw || !requiresAuth;

  const tags = Array.isArray(raw.tags)
    ? raw.tags
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];

  return {
    slug,
    name,
    description,
    requiresAuth,
    availableWhenSignedOut: normalizedAvailableWhenSignedOut,
    featureType,
    order,
    tags
  };
}
